import { UseQueryResult, useQuery } from "@tanstack/react-query";
import { useAuth, authorizedAxios } from "@moonscape/shared";
import { minutes } from "@/utils";

interface Referral {
  totalReferrals: number;
  totalRewards: number;
  claimableRewards: number;
}

interface UseGetReferralProps {
  refetchOnWindowFocus?: boolean;
  staleTime?: number;
}

export const useReferralSummaryQueryKey = () => ["GetRefferalSummary"] as const;

export const useGetReferralSummary = ({
  refetchOnWindowFocus = true,
  staleTime = 1 * minutes,
}: UseGetReferralProps): {
  isQueryLoading: boolean;
  isIdle: boolean;
  error: unknown;
  data?: Referral;
  refetch: () => void;
} => {
  const { user, getToken } = useAuth();
  const email = user?.email;

  const getRefferalSummary = async (): Promise<Referral> => {
    try {
      const token = await getToken();
      const res = await authorizedAxios(token).get(`/referrals`);
      return res.data;
    } catch (error) {
      console.error(`[useGetReferral] Failed to fetch referral`, error);
      throw error;
    }
  };

  const {
    isLoading: isQueryLoading,
    isIdle,
    error,
    data,
    refetch,
  }: UseQueryResult<Referral, unknown> = useQuery({
    queryKey: useReferralSummaryQueryKey(),
    queryFn: getRefferalSummary,
    enabled: !!email,
    refetchOnWindowFocus,
    staleTime,
    keepPreviousData: true,
  });

  return {
    isQueryLoading,
    isIdle,
    error,
    data,
    refetch,
  };
};
